// repo/primary-care-connect/frontend/src/api/clinicAdmin.ts
//
// Clinic Admin Portal API client (v1)
// - Built for Azure Static Web Apps + Azure Functions.
// - Uses cookie-based auth; do NOT add Authorization headers.
// - All endpoints are under /api/portal/clinic-admin/*
// - Sends x-acceptingmd-clinic-id from the selected Switch Role assignment.
//
// One goal: keep UI code clean and strongly typed.

import { clinicAdminScopeHeaders } from "../utils/portalRoleChoice";

export type ApiErrorBody = { error?: string };

export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody | unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function asString(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function asNumber(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function asNullableNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  return asNumber(v);
}

function isAbortSignal(v: unknown): v is AbortSignal {
  return (
    !!v &&
    typeof v === "object" &&
    "aborted" in v &&
    typeof (v as AbortSignal).addEventListener === "function"
  );
}

async function parseJsonSafe(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
      ...clinicAdminScopeHeaders(),
    },
    credentials: "include",
  });

  if (res.ok) {
    if (res.status === 204) return null as unknown as T;
    return (await parseJsonSafe(res)) as T;
  }

  const body = await parseJsonSafe(res);
  const msg =
    asString((body as any)?.error) ||
    `${res.status} ${res.statusText}` ||
    "Request failed";

  throw new ApiError(res.status, msg, body);
}

function get<T>(path: string, signal?: AbortSignal) {
  return request<T>(path, { method: "GET", signal });
}

function post<T>(path: string, body?: any, signal?: AbortSignal) {
  return request<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

function patch<T>(path: string, body?: any, signal?: AbortSignal) {
  return request<T>(path, {
    method: "PATCH",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

function put<T>(path: string, body?: any, signal?: AbortSignal) {
  return request<T>(path, {
    method: "PUT",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

/** ===== Types (Clinic Admin) ===== */

export type ClinicAdminClinicProfile = {
  clinicId: number;
  clinicName: string;
  governanceStatus?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  fax?: string | null;
  websiteUrl?: string | null;
  clinicSettings?: {
    hours?: ClinicAdminClinicHoursSettings;
  };
  [k: string]: any;
};

export type ClinicAdminNamedItem = { id: number; name: string };

export type ClinicAdminOperatingHour = {
  dayOfWeek: number;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
};

export type ClinicAdminClinicCore = {
  clinicId: number;
  name: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  cityId: number | null;
  cityName: string | null;
  postalCode: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  administrationEmail: string | null;
  latitude: number | null;
  longitude: number | null;
  clinicSettingId: number | null;
  clinicSettingName: string | null;
  governanceStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  [k: string]: any;
};

export type ClinicAdminClinicHoursByDay = Record<
  "0" | "1" | "2" | "3" | "4" | "5" | "6",
  | { isClosed: true }
  | { isClosed: false; open: string; close: string }
>;

export type ClinicAdminClinicHoursClosure = {
  date: string;
  reason: string | null;
};

export type ClinicAdminClinicTemporaryHoursOverride = {
  start: string;
  end: string;
  notePublic: string | null;
  hoursByDay: Partial<ClinicAdminClinicHoursByDay>;
};

export type ClinicAdminClinicWalkInHours = {
  enabled: boolean;
  notePublic: string | null;
  hoursByDay: Partial<ClinicAdminClinicHoursByDay>;
};

export type ClinicAdminClinicHoursSettings = {
  closures?: ClinicAdminClinicHoursClosure[];
  temporaryOverride?: ClinicAdminClinicTemporaryHoursOverride | null;
  walkIn?: ClinicAdminClinicWalkInHours | null;
};

export type ClinicAdminClinicSettings = {
  hours?: ClinicAdminClinicHoursSettings;
  [k: string]: any;
};

export type ClinicAdminClinicProfileResponse = {
  clinic: ClinicAdminClinicCore;
  clinicSettings: ClinicAdminClinicSettings;
  operatingHours: ClinicAdminOperatingHour[];
  onSiteServices: ClinicAdminNamedItem[];
  accessibilityFeatures: ClinicAdminNamedItem[];
  [k: string]: any;
};

export type ClinicAdminPatchClinicProfilePayload = {
  name?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  cityId?: number | null;
  postalCode?: string | null;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  administrationEmail?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  clinicSettingId?: number | null;
};

export type ClinicAdminPatchClinicProfileResponse = {
  ok: boolean;
  clinic: ClinicAdminClinicCore;
  [k: string]: any;
};

export type ClinicAdminPutClinicHoursPayload = {
  hours: Array<{
    dayOfWeek: number;
    isClosed: boolean;
    openTime: string | null;
    closeTime: string | null;
  }>;
};

export type ClinicAdminPutClinicHoursResponse = {
  ok: boolean;
  clinicId: number;
  hours: ClinicAdminOperatingHour[];
  [k: string]: any;
};

export type ClinicAdminPatchClinicSettingsPayload = {
  hours: ClinicAdminClinicHoursSettings;
};

export type ClinicAdminPatchClinicSettingsResponse = {
  ok: boolean;
  clinicId: number;
  clinicSettings: ClinicAdminClinicSettings;
  [k: string]: any;
};

export type ClinicAdminPutAccessibilityFeaturesPayload = {
  accessibilityFeatureIds: number[];
};

export type ClinicAdminPutAccessibilityFeaturesResponse = {
  ok: boolean;
  clinicId: number;
  accessibilityFeatureIds: number[];
  [k: string]: any;
};

export type ClinicAdminPutOnSiteServicesPayload = {
  onSiteServiceIds: number[];
};

export type ClinicAdminPutOnSiteServicesResponse = {
  ok: boolean;
  clinicId: number;
  onSiteServiceIds: number[];
  [k: string]: any;
};

export type ProviderStatusLabel = "Active" | "Inactive" | "Invited";

export type FamilyDoctorListItem = {
  familyDoctorId: number | null;
  providerInviteId?: number | null;
  fullName: string | null;
  cpso: string | null;
  gender: string | null;
  email: string | null;
  status: ProviderStatusLabel;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  acceptingNewPatients?: boolean | null;
  acceptingStatusUpdatedAt?: string | null;
  intakeNotes?: string | null;
  invitedAt?: string | null;
  inviteStatus?: string | null;
  [k: string]: any;
};

export type FamilyDoctorDetails = {
  familyDoctorId: number;
  fullName: string | null;
  cpso: string | null;
  gender: string | null;
  email: string | null;
  status: "Active" | "Inactive";
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  deactivatedByUserId?: number | null;
  acceptingNewPatients?: boolean | null;
  acceptingStatusUpdatedAt?: string | null;
  intakeNotes?: string | null;
  firstAppointmentBookingUrl: string | null;
  declaredCapacity: number | null;
  remainingCapacityOverride: number | null;
  remainingCapacityUpdatedAt: string | null;
  [k: string]: any;
};

export type FamilyDoctorLookupItem = {
  id: number;
  name: string;
};

export type FamilyDoctorDetailsResponse = {
  doctor: FamilyDoctorDetails & {
    userId?: number | null;
    clinicId?: number | null;
    [k: string]: any;
  };
  languages: FamilyDoctorLookupItem[];
  visitTypes: FamilyDoctorLookupItem[];
  coverageTypes: FamilyDoctorLookupItem[];
  intakeRules: ClinicAdminFamilyDoctorIntakeRule[];
  [k: string]: any;
};

export type ClinicAdminFamilyDoctorIntakeRule = {
  id: number;
  familyDoctorId: number;
  criterionKey: string | null;
  criterionValue: string | null;
  matchBehavior: string | null;
  weight: number | null;
  isHardRule: boolean;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type FamilyDoctorAcceptingStatusResponse = {
  familyDoctorId: number;
  acceptingNewPatients: boolean;
  acceptingStatusUpdatedAt: string | null;
  updatedAt: string | null;
  heartbeatAt: string | null;
};

export type SpecialistListItem = {
  specialistId: number;
  fullName: string | null;
  cpso: string | null;
  gender: string | null;
  email: string | null;
  specialtyId?: number | null;
  status: ProviderStatusLabel;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  acceptingNewReferrals?: boolean | null;
  intakeNotes?: string | null;
  invitedAt?: string | null;
  inviteStatus?: string | null;
  [k: string]: any;
};

export type SpecialistDetails = {
  specialistId: number;
  fullName: string | null;
  cpso: string | null;
  gender: string | null;
  email: string | null;
  specialtyId?: number | null;
  status: "Active" | "Inactive";
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  deactivatedByUserId?: number | null;
  acceptingNewReferrals?: boolean | null;
  intakeNotes?: string | null;
  [k: string]: any;
};

export type InviteProviderPayload = {
  cpso?: string | null;
  fullName?: string | null;
  email?: string | null;
  gender?: "Male" | "Female" | null;
  specialtyId?: number | null;
};

export type InviteActionResult = {
  ok?: boolean;
  success?: boolean;
  message?: string;
  [k: string]: any;
};

export type ListEnvelope<T> = {
  clinicId: number;
  clinicName: string;
  items: T[];
  [k: string]: any;
};

export type FamilyDoctorCpsoLookupItem = {
  familyDoctorId: number | null;
  fullName: string | null;
  cpso: string | null;
  email: string | null;
};

export type FamilyDoctorCpsoLookupResponse = {
  cpso: string;
  found: boolean;
  items: FamilyDoctorCpsoLookupItem[];
};

export type PatientRequestsSummaryTotals = {
  total: number;
  awaitingReview: number;
  acceptedPendingAttachment: number;
  meetAndGreetBacklog?: number;
  attachmentsCompleted: number;
};

export type PatientRequestsCapacitySummary = {
  doctorCount: number;
  doctorsWithCapacitySignal: number;
  declaredCapacity: number | null;
  usedCapacity: number;
  remainingCapacityOverride: number | null;
  remainingCapacity: number | null;
  remainingCapacityUpdatedAt: string | null;
  capacitySource: "missing" | "computed" | "override" | string;
};

export type PatientRequestsThroughputSummary = {
  attachments30d: number;
  attachmentsInRange?: number;
  rangeDays?: number;
  pacePerDay: number;
  avgDaysToAttachment: number | null;
  lastAttachmentCompletedAt: string | null;
};

export type PatientRequestsForecastSummary = {
  estimatedDaysToFullCapacity: number | null;
  forecastStatus:
    | "ready"
    | "capacityReached"
    | "capacityNotSet"
    | "notEnoughPace"
    | string;
  forecastMessage: string;
};

export type PatientRequestsPipelineSummary = {
  submitted: number;
  inReview: number;
  waitlist: number;
  acceptedPendingAttachment: number;
  totalActive: number;
};

export type MeetAndGreetBlockedDoctor = {
  familyDoctorId: number;
  doctorName: string | null;
  backlogCount: number;
};

export type MeetAndGreetLimitsSummary = {
  limit: number;
  blockedDoctors: MeetAndGreetBlockedDoctor[];
};

export type PatientRequestsSummaryResponse = {
  clinicId: number;
  mode: "summary";
  filters: {
    status: string | null;
    statusId: number | null;
    familyDoctorProfileId: number | null;
    q: string | null;
    from: string | null;
    to: string | null;
    baseline: "all" | "unattached" | "attached";
    sourceScope?: "public" | "navigator" | "all" | null;
  };
  totals: PatientRequestsSummaryTotals;
  byStatus: Record<string, number>;
  capacity?: PatientRequestsCapacitySummary | null;
  throughput?: PatientRequestsThroughputSummary | null;
  forecast?: PatientRequestsForecastSummary | null;
  pipeline?: PatientRequestsPipelineSummary | null;
  meetAndGreetLimits?: MeetAndGreetLimitsSummary | null;
};

export type ClinicAdminPatientRequestReason = {
  id: number;
  name: string;
};

export type ClinicAdminPatientRequestActivity = {
  id: number | string;
  activityType: string | null;
  activityTypeLabel?: string | null;
  outcomeCode: string | null;
  label?: string | null;
  note: string | null;
  createdAt: string | null;
  createdByUserId: number | null;
  createdByRole: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  isSystemGenerated: boolean;
};

export type ClinicAdminPriorityTag = {
  key: string;
  label: string;
  kind: "system" | string;
  basis?: string | null;
};

export type ClinicAdminNavigatorContext = {
  isNavigatorAssisted?: boolean | null;
  navigatorCaseId: number | null;
  sourceType: string | null;
  sourceLabel: string | null;
  publicSummary?: string | null;
  forwardedByNavigatorUserId: number | null;
  forwardedByNavigatorName: string | null;
  forwardedAt: string | null;
};

export type ClinicAdminPatientRequestListItem = {
  requestId: number;
  patientLeadId: number;

  familyDoctorProfileId: number;
  familyDoctorName: string | null;
  familyDoctorCpso: string | null;

  statusId: number;
  statusName: string;
  rawStatusName?: string | null;
  rawStatus?: string | null;

  requestFor: string | null;
  source: string | null;

  createdAt?: string | null;
  updatedAt?: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
  decisionAt: string | null;
  cancelledAt: string | null;
  autoExpiredAt: string | null;
  attachmentCompletedAt: string | null;

  otherReasonText: string | null;

  patient: {
    fullName: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    postalCode: string | null;
    email: string | null;
    phoneNumber: string | null;
    applicantEmail: string | null;
    applicantPhone: string | null;
  };

  reasons: ClinicAdminPatientRequestReason[];

  // Safe system-derived request tags.
  // These are shared by public and Navigator requests and do not expose Navigator matching logic.
  priorityTags?: ClinicAdminPriorityTag[];

  statusHistory?: ClinicAdminRequestStatusHistoryItem[];
  activities?: ClinicAdminPatientRequestActivity[];
  timeline?: {
    createdAt?: string | null;
    submittedAt?: string | null;
    verifiedAt?: string | null;
    decisionAt?: string | null;
    cancelledAt?: string | null;
    autoExpiredAt?: string | null;
    attachmentCompletedAt?: string | null;
    forwardedAt?: string | null;
    statusHistory?: ClinicAdminRequestStatusHistoryItem[];
    activities?: ClinicAdminPatientRequestActivity[];
    [k: string]: any;
  };

  navigator?: ClinicAdminNavigatorContext | null;

  navigatorCaseId?: number | null;
  sourceType?: string | null;
  sourceLabel?: string | null;
  forwardedByNavigatorUserId?: number | null;
  forwardedByNavigatorName?: string | null;
  forwardedAt?: string | null;

  [k: string]: any;
};

export type ClinicAdminPatientRequestsListResponse = {
  clinicId: number;
  paging: {
    total: number;
    limit: number;
    offset: number;
  };
  filters: {
    status: string | null;
    statusId: number | null;
    familyDoctorProfileId: number | null;
    q: string | null;
    from: string | null;
    to: string | null;
    baseline: "all" | "unattached" | "attached";
    sourceScope?: "public" | "navigator" | "all" | null;
  };
  items: ClinicAdminPatientRequestListItem[];
};

export type ClinicAdminRequestStatusHistoryItem = {
  id: number;
  fromStatusId: number | null;
  toStatusId: number | null;
  changedByUserId: number | null;
  note: string | null;
  changedAt: string;
};

export type ClinicAdminPatientRequestDetailsResponse = {
  clinicId: number;
  request: ClinicAdminPatientRequestListItem & {
    consentAccepted?: boolean | null;
    consentAcceptedAt?: boolean | null;
    consentVersion?: string | null;
    statusHistory?: ClinicAdminRequestStatusHistoryItem[];
    activities?: ClinicAdminPatientRequestActivity[];
    timeline?: {
      createdAt?: string | null;
      submittedAt?: string | null;
      verifiedAt?: string | null;
      decisionAt?: string | null;
      cancelledAt?: string | null;
      autoExpiredAt?: string | null;
      attachmentCompletedAt?: string | null;
      forwardedAt?: string | null;
      statusHistory?: ClinicAdminRequestStatusHistoryItem[];
      activities?: ClinicAdminPatientRequestActivity[];
      [k: string]: any;
    };
  };
};

export type ClinicAdminPatientRequestsListArgs = {
  status?: string | null;
  statusId?: number | null;
  familyDoctorProfileId?: number | null;
  q?: string | null;
  from?: string | null;
  to?: string | null;
  baseline?: "all" | "unattached" | "attached" | null;
  sourceScope?: "public" | "navigator" | "all" | null;
  limit?: number | null;
  offset?: number | null;
};

export type ClinicAdminActionResult = {
  success?: boolean;
  ok?: boolean;
  requestId?: number;
  action?: string;
  targetStatus?: string;
  statusName?: string;
  message?: string;
  [k: string]: any;
};

export type ClinicAdminPostAcceptanceAction =
  | "Declined"
  | "PatientWithdrew"
  | "PatientAcceptedElsewhere"
  | "NoShow"
  | "ClinicDeclinedAfterMeetAndGreet"
  | "MovedToWaitlist";

export type ClinicAdminDeclinePostAcceptancePayload = {
  action?: ClinicAdminPostAcceptanceAction;
  decision?: ClinicAdminPostAcceptanceAction;
  outcomeCode?: ClinicAdminPostAcceptanceAction;
  note?: string | null;
};

export type ClinicAdminAttachmentCompletePayload = {
  note?: string | null;
};

function buildQuery(params: Record<string, string | number | null | undefined>) {
  const sp = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined) continue;
    const s = String(v);
    if (!s.length) continue;
    sp.set(k, s);
  }

  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/** ===== Mappers ===== */

function mapFamilyDoctorListItem(raw: any): FamilyDoctorListItem {
  const familyDoctorId = asNumber(
    raw?.familyDoctorId ?? raw?.FamilyDoctorID ?? raw?.FamilyDoctorId
  );
  const providerInviteId = asNumber(
    raw?.providerInviteId ??
      raw?.ProviderInviteID ??
      raw?.ProviderInviteId ??
      raw?.inviteId ??
      raw?.InviteID
  );

  const fullName = asString(raw?.fullName ?? raw?.FullName);
  const cpso = asString(raw?.cpso ?? raw?.CPSO ?? raw?.Cpso);
  const gender = asString(raw?.gender ?? raw?.Gender);
  const email = asString(raw?.email ?? raw?.Email);

  const statusRaw = asString(raw?.status ?? raw?.Status) || "Active";
  const status: ProviderStatusLabel =
    statusRaw === "Invited" || statusRaw === "Inactive" ? statusRaw : "Active";

  return {
    familyDoctorId: familyDoctorId ?? null,
    providerInviteId: providerInviteId ?? null,

    fullName,
    cpso,
    gender,
    email,
    status,

    activatedAt: asString(raw?.activatedAt ?? raw?.ActivatedAt),
    deactivatedAt: asString(raw?.deactivatedAt ?? raw?.DeactivatedAt),

    acceptingNewPatients:
      raw?.acceptingNewPatients ?? raw?.AcceptingNewPatients ?? null,
    acceptingStatusUpdatedAt: asString(
      raw?.acceptingStatusUpdatedAt ?? raw?.AcceptingStatusUpdatedAt
    ),
    intakeNotes: asString(raw?.intakeNotes ?? raw?.IntakeNotes),

    invitedAt: asString(raw?.invitedAt ?? raw?.InvitedAt),
    inviteStatus: asString(raw?.inviteStatus ?? raw?.InviteStatus),

    ...raw,
  };
}

/** ===== Endpoint helpers ===== */

const BASE = "/api/portal/clinic-admin";

export const clinicAdminApi = {
  getClinicProfile: (signal?: AbortSignal) =>
    get<ClinicAdminClinicProfileResponse>(`${BASE}/clinic-profile`, signal),

  patchClinicProfile: (
    payload: ClinicAdminPatchClinicProfilePayload,
    signal?: AbortSignal
  ) =>
    patch<ClinicAdminPatchClinicProfileResponse>(
      `${BASE}/clinic-profile`,
      payload,
      signal
    ),

  putClinicHours: (
    payload: ClinicAdminPutClinicHoursPayload,
    signal?: AbortSignal
  ) =>
    put<ClinicAdminPutClinicHoursResponse>(
      `${BASE}/clinic-hours`,
      payload,
      signal
    ),

  patchClinicSettings: (
    payload: ClinicAdminPatchClinicSettingsPayload,
    signal?: AbortSignal
  ) =>
    patch<ClinicAdminPatchClinicSettingsResponse>(
      `${BASE}/clinic-settings`,
      payload,
      signal
    ),

  putClinicAccessibilityFeatures: (
    payload: ClinicAdminPutAccessibilityFeaturesPayload,
    signal?: AbortSignal
  ) =>
    put<ClinicAdminPutAccessibilityFeaturesResponse>(
      `${BASE}/clinic-accessibility-features`,
      payload,
      signal
    ),

  putClinicOnSiteServices: (
    payload: ClinicAdminPutOnSiteServicesPayload,
    signal?: AbortSignal
  ) =>
    put<ClinicAdminPutOnSiteServicesResponse>(
      `${BASE}/clinic-on-site-services`,
      payload,
      signal
    ),

  listFamilyDoctors: async (signal?: AbortSignal) => {
    const env = await get<any>(`${BASE}/family-doctors`, signal);
    const itemsRaw = Array.isArray(env?.items) ? env.items : [];
    const items = itemsRaw.map(mapFamilyDoctorListItem);

    return {
      ...env,
      clinicId: Number(env?.clinicId ?? env?.ClinicID ?? env?.ClinicId) || 0,
      clinicName: asString(env?.clinicName ?? env?.ClinicName) || "",
      items,
    } as ListEnvelope<FamilyDoctorListItem>;
  },

  getFamilyDoctor: async (familyDoctorId: number, signal?: AbortSignal) => {
    const env = await get<any>(
      `${BASE}/family-doctors/${familyDoctorId}`,
      signal
    );
    const rawDoctor = env?.doctor || {};

    return {
      ...env,
      doctor: {
        ...rawDoctor,
        firstAppointmentBookingUrl: asString(
          rawDoctor.firstAppointmentBookingUrl ??
            rawDoctor.FirstAppointmentBookingUrl
        ),
        declaredCapacity: asNullableNumber(
          rawDoctor.declaredCapacity ?? rawDoctor.DeclaredCapacity
        ),
        remainingCapacityOverride: asNullableNumber(
          rawDoctor.remainingCapacityOverride ??
            rawDoctor.RemainingCapacityOverride
        ),
        remainingCapacityUpdatedAt: asString(
          rawDoctor.remainingCapacityUpdatedAt ??
            rawDoctor.RemainingCapacityUpdatedAt
        ),
      },
      intakeRules: Array.isArray(env?.intakeRules) ? env.intakeRules : [],
    } as FamilyDoctorDetailsResponse;
  },

  lookupFamilyDoctorByCpso: (cpso: string, signal?: AbortSignal) => {
    const cpsoNorm = String(cpso || "").trim();

    if (!cpsoNorm) {
      throw new Error("CPSO is required");
    }

    const url = `${BASE}/family-doctors${buildQuery({ cpso: cpsoNorm })}`;
    return get<FamilyDoctorCpsoLookupResponse>(url, signal);
  },

  inviteFamilyDoctor: (payload: InviteProviderPayload, signal?: AbortSignal) =>
    post<InviteActionResult>(`${BASE}/family-doctors/invite`, payload, signal),

  resendFamilyDoctorInvite: (providerInviteId: number, signal?: AbortSignal) =>
    post<InviteActionResult>(
      `${BASE}/family-doctors/invite/resend`,
      { providerInviteId },
      signal
    ),

  revokeFamilyDoctorInvite: (providerInviteId: number, signal?: AbortSignal) =>
    post<InviteActionResult>(
      `${BASE}/family-doctors/invite/revoke`,
      { providerInviteId },
      signal
    ),

  deactivateFamilyDoctor: (familyDoctorId: number, signal?: AbortSignal) =>
    post<InviteActionResult>(
      `${BASE}/family-doctors/${familyDoctorId}/deactivate`,
      undefined,
      signal
    ),

  reactivateFamilyDoctor: (familyDoctorId: number, signal?: AbortSignal) =>
    post<InviteActionResult>(
      `${BASE}/family-doctors/${familyDoctorId}/reactivate`,
      undefined,
      signal
    ),

  setFamilyDoctorAcceptingStatus: (
    familyDoctorId: number,
    acceptingNewPatients: boolean,
    signal?: AbortSignal
  ) =>
    put<FamilyDoctorAcceptingStatusResponse>(
      `${BASE}/family-doctors/${familyDoctorId}/accepting-status`,
      { acceptingNewPatients },
      signal
    ),

  listSpecialists: (signal?: AbortSignal) =>
    get<ListEnvelope<SpecialistListItem>>(`${BASE}/specialists`, signal),

  getSpecialist: (specialistId: number, signal?: AbortSignal) =>
    get<SpecialistDetails>(`${BASE}/specialists/${specialistId}`, signal),

  inviteSpecialist: (payload: InviteProviderPayload, signal?: AbortSignal) =>
    post<InviteActionResult>(`${BASE}/specialists-invite`, payload, signal),

  resendSpecialistInvite: (providerInviteId: number, signal?: AbortSignal) =>
    post<InviteActionResult>(
      `${BASE}/specialists-invite-resend`,
      { providerInviteId },
      signal
    ),

  getPatientRequestsSummary: (
    args: {
      familyDoctorProfileId?: number | null;
      from?: string | null;
      to?: string | null;
      toMode?: "exclusive" | "inclusive" | null;
      baseline?: "all" | "unattached" | "attached" | null;
      sourceScope?: "public" | "navigator" | "all" | null;
    },
    signal?: AbortSignal
  ) => {
    const qs = buildQuery({
      summary: 1,
      familyDoctorProfileId: args.familyDoctorProfileId ?? null,
      from: args.from ?? null,
      to: args.to ?? null,
      toMode: args.toMode ?? null,
      baseline: args.baseline ?? null,
      sourceScope: args.sourceScope ?? null,
    });

    return get<PatientRequestsSummaryResponse>(
      `${BASE}/patient-requests${qs}`,
      signal
    );
  },

  listPatientRequests: (
    args: ClinicAdminPatientRequestsListArgs = {},
    signal?: AbortSignal
  ) => {
    const qs = buildQuery({
      status: args.status ?? null,
      statusId: args.statusId ?? null,
      familyDoctorProfileId: args.familyDoctorProfileId ?? null,
      q: args.q ?? null,
      from: args.from ?? null,
      to: args.to ?? null,
      baseline: args.baseline ?? null,
      sourceScope: args.sourceScope ?? null,
      limit: args.limit ?? null,
      offset: args.offset ?? null,
    });

    return get<ClinicAdminPatientRequestsListResponse>(
      `${BASE}/patient-requests${qs}`,
      signal
    );
  },

  getPatientRequestDetails: (requestId: number, signal?: AbortSignal) => {
    if (!Number.isFinite(requestId) || requestId <= 0) {
      throw new Error("Invalid requestId");
    }

    return get<ClinicAdminPatientRequestDetailsResponse>(
      `${BASE}/patient-requests/${requestId}`,
      signal
    );
  },

  attachmentComplete: (
    requestId: number,
    payloadOrSignal?: ClinicAdminAttachmentCompletePayload | AbortSignal,
    signal?: AbortSignal
  ) => {
    if (!Number.isFinite(requestId) || requestId <= 0) {
      throw new Error("Invalid requestId");
    }

    const payload = isAbortSignal(payloadOrSignal) ? undefined : payloadOrSignal;
    const effectiveSignal = isAbortSignal(payloadOrSignal)
      ? payloadOrSignal
      : signal;

    return post<ClinicAdminActionResult>(
      `${BASE}/patient-requests/${requestId}/attachment-complete`,
      payload,
      effectiveSignal
    );
  },

  declinePostAcceptance: (
    requestId: number,
    payloadOrSignal?: ClinicAdminDeclinePostAcceptancePayload | AbortSignal,
    signal?: AbortSignal
  ) => {
    if (!Number.isFinite(requestId) || requestId <= 0) {
      throw new Error("Invalid requestId");
    }

    const payload = isAbortSignal(payloadOrSignal) ? undefined : payloadOrSignal;
    const effectiveSignal = isAbortSignal(payloadOrSignal)
      ? payloadOrSignal
      : signal;

    return post<ClinicAdminActionResult>(
      `${BASE}/patient-requests/${requestId}/decline-post-acceptance`,
      payload,
      effectiveSignal
    );
  },
};